/* eslint-disable no-unused-vars */
import React, { createContext, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { trackEvent } from '../../utils/functions'
import HomeVUService from '../../services/HomeVU/HomeVU.service'

export const HomeVUContext = createContext()

export const HomeVUContextProvider = (props) => {
  const [summaryAnalytics, setSummaryAnalytics] = useState(null)
  const [allFiles, setAllFiles] = useState([])
  const [allDevices, setAllDevices] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [allScreens, setAllScreens] = useState([])
  const [scheduledMeetings, setScheduledMeetings] = useState([])
  const [tips, setTips] = useState([])

  // Files and Recordings
  const sortbyDate = useCallback((a, b) => {
    return new Date(b.LastModified) - new Date(a.LastModified)
  }, [])

  const fetchFiles = useCallback(async (portalName) => {
    try {
      const res = await HomeVUService.fetchFiles(portalName)
      setAllFiles(res.data)
    } catch (error) {
      // Error fetching files
    }
  }, [])

  const viewFile = useCallback(async (portalName, key) => {
    try {
      trackEvent('Viewed Recorded Video')
      return await HomeVUService.viewFile(portalName, key)
    } catch (error) {
      // Error viewing file
    }
  }, [])

  const viewFileById = useCallback(async (portalName, id) => {
    try {
      trackEvent('Viewed Recorded Video')
      return await HomeVUService.viewFileById(portalName, id)
    } catch (error) {
      // Error viewing file by ID
    }
  }, [])

  // Past Calls
  const fetchSummaryAnalytics = useCallback(async (portalName, userId) => {
    try {
      const res = await HomeVUService.fetchSummaryAnalytics(portalName, userId)
      setSummaryAnalytics(res.data)
    } catch (error) {
      // Error fetching summary analytics
    }
  }, [])

  // Scheduling Modal
  const fetchDevices = useCallback((portalName) => {
    HomeVUService.fetchDevices(portalName).then((res) => {
      setAllDevices(res.data)
    })
  }, [])

  const fetchUsers = useCallback(async (portalName) => {
    try {
      const res = await HomeVUService.fetchUsers(portalName)
      setAllUsers(res.data)
    } catch (err) {
      // Error fetching users
    }
  }, [])

  const fetchScreens = useCallback(async (portalName) => {
    try {
      const res = await HomeVUService.fetchScreens(portalName)
      setAllScreens(res.data)
    } catch (err) {
      // Error fetching screens
    }
  }, [])

  const createScheduledMeeting = useCallback(async (param) => {
    try {
      return await HomeVUService.createScheduledMeeting(param)
    } catch (err) {
      // Error creating scheduled meeting
    }
  }, [])

  const deleteScheduledMeeting = useCallback(async (id) => {
    try {
      return await HomeVUService.deleteScheduledMeeting(id)
    } catch (err) {
      // Error deleting scheduled meeting
    }
  }, [])

  const editScheduledMeeting = useCallback(async (id, param) => {
    try {
      return await HomeVUService.editScheduledMeeting(id, param)
    } catch (err) {
      // Error editing scheduled meeting
    }
  }, [])

  const getScheduledMeeting = useCallback(async (id) => {
    try {
      return await HomeVUService.getScheduledMeeting(id)
    } catch (err) {
      // Error getting scheduled meeting
    }
  }, [])

  const getScheduledMeetings = useCallback(
    async (portalName, id, userId, startDate, endDate) => {
      try {
        const res = await HomeVUService.getScheduledMeetings(
          portalName,
          id,
          userId,
          startDate,
          endDate
        )
        setScheduledMeetings(res.data)
      } catch (err) {
        // Error getting scheduled meetings
      }
    },
    []
  )

  // Did You Know
  const getTips = useCallback(async (portalName, isActive) => {
    try {
      const response = await HomeVUService.getTips(portalName || '', isActive)
      setTips(response.data)
    } catch (err) {
      // Error getting tips
    }
  }, [])

  const linkCall = useCallback(async (sessionId, meetingId) => {
    try {
      return await HomeVUService.linkCall(sessionId, meetingId)
    } catch (err) {
      // Error linking call
    }
  }, [])

  // Provider Availability
  const getProviderAvailability = useCallback(async (portalName) => {
    try {
      const res = await HomeVUService.getProviderAvailability(portalName)
      return res.data
    } catch (err) {
      // Error fetching provider availability
      return []
    }
  }, [])

  const saveProviderAvailability = useCallback(async (params) => {
    try {
      const res = await HomeVUService.saveProviderAvailability(params)
      return res.data
    } catch (err) {
      // Error saving provider availability
      return null
    }
  }, [])

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      summaryAnalytics,
      fetchSummaryAnalytics,
      allFiles,
      fetchFiles,
      viewFile,
      viewFileById,
      allDevices,
      allUsers,
      fetchDevices,
      fetchUsers,
      fetchScreens,
      allScreens,
      createScheduledMeeting,
      editScheduledMeeting,
      deleteScheduledMeeting,
      getScheduledMeeting,
      getScheduledMeetings,
      scheduledMeetings,
      getTips,
      tips,
      linkCall,
      getProviderAvailability,
      saveProviderAvailability
    }),
    [
      summaryAnalytics,
      fetchSummaryAnalytics,
      allFiles,
      fetchFiles,
      viewFile,
      viewFileById,
      allDevices,
      allUsers,
      fetchDevices,
      fetchUsers,
      fetchScreens,
      allScreens,
      createScheduledMeeting,
      editScheduledMeeting,
      deleteScheduledMeeting,
      getScheduledMeeting,
      getScheduledMeetings,
      scheduledMeetings,
      getTips,
      tips,
      linkCall,
      getProviderAvailability,
      saveProviderAvailability
    ]
  )

  return (
    <HomeVUContext.Provider value={contextValue}>
      {props.children}
    </HomeVUContext.Provider>
  )
}

HomeVUContextProvider.propTypes = {
  children: PropTypes.object
}
